import { withAuthenticationRequired } from "@auth0/auth0-react";
import Loading from "../components/common/Loading";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useReservationAPI } from "../service/useReservationAPI";
import { Button, Card, CardActions, Grid, useMediaQuery, useTheme, Box, Typography, Divider } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { GlobalStateContext } from "../state/GlobalStateProvider";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import AppSnackbar from "../common/AppSnackbar";
import {formatDateTime} from "../common/DateUtil";
import {useTranslation} from "react-i18next";
import { useTenantConfig } from "../security/tenant-config-context";
import { useAppointmentsAPI } from "../service/useAppointmentsAPI";
import StatusBadge from "../components/common/StatusBadge";

const MyReservations = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { updateGlobalState, setPartOfGlobalState } = useContext(GlobalStateContext);
    const { getMyReservations } = useReservationAPI();
    const { getMyAppointments } = useAppointmentsAPI();
    const { cfg } = useTenantConfig();
    const isAppointmentFlow = cfg?.businessTypes?.includes('APPOINTMENT_BASED');
    const [reservations, setReservations] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const fetchReservations = async () => {
        try {
            updateGlobalState({ reservationUuid: '' });
            const value = isAppointmentFlow ? await getMyAppointments() : await getMyReservations();
            setReservations(value || []);
        } catch (reason) {
            handleFetchError(t('fetchReservationsError'));
        } finally {
            setIsLoading(false);
        }
    };

    function handleFetchError(reason) {
        setSnackbarMessage(reason);
        setOpenSnackbar(true);
    }

    useEffect(() => {
        if (isLoading) {
            fetchReservations().then();
        }
    },[reservations]);

    const columns: GridColDef[] = useMemo(() => {
        if (isAppointmentFlow) {
            return [
                {
                    field: 'startAt',
                    headerName: t('start'),
                    flex: 1,
                    valueGetter: (params) => {
                        const v = params.row.startAt;
                        return v ? new Date(v).toLocaleString([], {hour: '2-digit', minute: '2-digit', year: 'numeric', month: '2-digit', day: '2-digit'}) : '';
                    },
                },
                {
                    field: 'endAt',
                    headerName: t('end'),
                    flex: 1,
                    valueGetter: (params) => {
                        const v = params.row.endAt;
                        return v ? new Date(v).toLocaleString([], {hour: '2-digit', minute: '2-digit', year: 'numeric', month: '2-digit', day: '2-digit'}) : '';
                    },
                },
                {
                    field: 'service',
                    headerName: t('service'),
                    flex: 1,
                    valueGetter: (params) => params.row.serviceName || params.row.service || params.row.serviceUuid || '',
                },
                {
                    field: 'staff',
                    headerName: t('employee'),
                    flex: 1,
                    valueGetter: (params) => params.row.staffName || params.row.staff || params.row.staffUuid || '',
                },
                {
                    field: 'animal',
                    headerName: t('for'),
                    flex: 1,
                    valueGetter: (params) => params.row.animalName || params.row.animal || params.row.animalUuid || '',
                },
                {
                    field: 'status',
                    headerName: t('status'),
                    flex: 1,
                    valueGetter: (params) => params.row.animalName || params.row.animal || params.row.animalUuid || '',
                    renderCell: (params) => {
                        return <StatusBadge status={params.row.status}/>;
                    }
                }
            ];
        }
        return [
            {
                field: 'checkIn',
                headerName: t('checkIn'),
                flex: 1,
                valueGetter: (params) => {
                    const fromDate = params.row.fromDate;
                    const checkInHour = params.row.checkInHour;
                    return formatDateTime(fromDate, checkInHour ? checkInHour : '');
                },
            },
            {
                field: 'checkOut',
                headerName: t('checkOut'),
                flex: 1,
                valueGetter: (params) => {
                    const toDate = params.row.toDate;
                    const checkOutHour = params.row.checkOutHour;
                    return formatDateTime(toDate, checkOutHour ? checkOutHour : '');
                },
            },
            {
                field: 'aantal',
                headerName: t('for'),
                flex: 1,
                valueGetter: (params) => {
                    return `${params.row.animals.map(animal => animal.name).join(', ')}`;
                },
            },
            {
                field: 'stayTypes',
                headerName: t('stayType'),
                flex: 1,
                valueGetter: (params) => {
                    return `${params.row.stays.map(value => value.name).join(', ')}`;
                },
            }
        ];
    }, [isAppointmentFlow, t]);

    function getRowId(row) {
        return row.uuid;
    }

    function clearReservationMetaAndNavigate() {
        setPartOfGlobalState('reservationMeta', {
            fromDate: undefined,
            toDate: undefined,
            animalsInReservationIds: [],
            checkInHour: undefined,
            checkOutHour: undefined,
            stays: []
        });
        navigate('/nieuwe-reservatie');
    }

    const MobileReservationCard = ({ row }) => (
        <Card variant="outlined" sx={{ mb: 2, textAlign: 'left', p: 2 }}>
            {isAppointmentFlow ? (
                <>
                    <Typography variant="body2"><strong>{t('start')}:</strong> {row.startAt ? new Date(row.startAt).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'}) : ''}</Typography>
                    <Typography variant="body2"><strong>{t('end')}:</strong> {row.endAt ? new Date(row.endAt).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'}) : ''}</Typography>
                    <Typography variant="body2"><strong>{t('service')}:</strong> {row.serviceName || row.service || row.serviceUuid || ''}</Typography>
                    <Typography variant="body2"><strong>{t('employee')}:</strong> {row.staffName || row.staff || row.staffUuid || ''}</Typography>
                    <Typography variant="body2"><strong>{t('for')}:</strong> {row.animalName || row.animal || row.animalUuid || ''}</Typography>
                </>
            ) : (
                <>
                    <Typography variant="body2"><strong>{t('checkIn')}:</strong> {formatDateTime(row.fromDate, row.checkInHour || '')}</Typography>
                    <Typography variant="body2"><strong>{t('checkOut')}:</strong> {formatDateTime(row.toDate, row.checkOutHour || '')}</Typography>
                    <Typography variant="body2"><strong>{t('for')}:</strong> {row.animals.map(a => a.name).join(', ')}</Typography>
                    <Typography variant="body2"><strong>{t('stayType')}:</strong> {row.stays.map(s => s.name).join(', ')}</Typography>
                </>
            )}
        </Card>
    );

    return (
        <>
            {isLoading && (<Loading />)}
            {!isLoading && (
                <Grid container alignItems="center" justifyContent="center" spacing={1} sx={{textAlign: 'center', mt:4, p: isMobile ? 2 : 0}}>
                    {isMobile ? (
                        <Box sx={{ width: '100%' }}>
                            {reservations.length > 10 && (
                                <Button variant="contained" fullWidth onClick={clearReservationMetaAndNavigate} data-testid="make-new-reservation-button-mobile-top" sx={{ mb: 2 }}>
                                    {t(isAppointmentFlow ? 'makeAppointment' : 'makeReservation')}
                                </Button>
                            )}
                            {reservations.length > 0 ? (
                                reservations.map(reservation => (
                                    <MobileReservationCard key={reservation.uuid} row={reservation} />
                                ))
                            ) : (
                                <Typography sx={{ mb: 2 }}>{t('noReservations')}</Typography>
                            )}
                            <Button variant="contained" fullWidth onClick={clearReservationMetaAndNavigate} data-testid="make-new-reservation-button-mobile">
                                {t(isAppointmentFlow ? 'makeAppointment' : 'makeReservation')}
                            </Button>
                        </Box>
                    ) : (
                        <Card variant="outlined" sx={{ width: '100%', maxWidth: '900px', mb: 2 }}>
                            <DataGrid
                                rows={reservations}
                                getRowId={getRowId}
                                columns={columns}
                                initialState={{
                                    pagination: {
                                        paginationModel: { page: 0, pageSize: 5 },
                                    },
                                }}
                                localeText={{
                                    MuiTablePagination: {
                                        labelRowsPerPage: t('itemsPerPage')
                                    },
                                }}
                                pageSizeOptions={[5, 10]}
                                autoHeight
                                disableSelectionOnClick
                                disableColumnFilter
                                disableColumnMenu
                                data-testid="reservations-datagrid"
                            />
                            <CardActions sx={{ justifyContent: 'flex-end' }}>
                                <Button variant="contained" onClick={clearReservationMetaAndNavigate} data-testid="make-new-reservation-button">
                                    {t(isAppointmentFlow ? 'makeAppointment' : 'makeReservation')}
                                </Button>
                            </CardActions>
                        </Card>
                    )}
                    <AppSnackbar openSnackbar={openSnackbar} snackbarMessage={snackbarMessage} setOpenSnackbar={setOpenSnackbar} />
                </Grid>
            )}
        </>
    );
}

export default withAuthenticationRequired(MyReservations, {
    onRedirecting: () => <Loading />,
    returnTo: '/nieuwe-reservatie'
});
